// ============================================================================
// Sub Replacement data layer (Phase 1).
//
// Surface matches the supply/hiring modules: a list hook + a create helper +
// form-options hooks for the facility / outgoing-assignment pickers.
//
// All reads honor RLS:
//   - AMs see requests on their own facilities (via is_my_facility)
//   - has_full_am_authority roles see everything
//   - other tcs_employee roles see everything read-only
//
// `request_id` is auto-assigned by the BEFORE INSERT trigger as SRR-NNNN.
// Frontend MUST NOT supply it.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import type {
  NewSubReplacementRequestInput,
  SubReplacementRequest,
  SubReplacementRequestRow,
  SubReplacementStatus,
} from '../../../shared/types';
import { errorMessage } from '../../../shared/errors';
import { catalogLabel } from '../../../shared/service-roles';

// ---------------------------------------------------------------------------
// Flat select — no PostgREST resource embedding. See
// docs/database-changes.md for the rationale: embedding requires FKs to be
// present in the DB AND in PostgREST's schema cache. The flat-fetch +
// client-join pattern is FK-independent and survives migrations cleanly.
// ---------------------------------------------------------------------------

const REQUEST_FLAT_SELECT = `
  request_id, facility_id, account_id, outgoing_assignment_id,
  incoming_provider_id, requested_by_eid, reason, reason_code, urgency, hold_pay,
  incoming_pay_rate, nmi_enabled, nmi_amount,
  compliment_enabled, compliment_amount,
  planned_change_date, change_date_type,
  notes, status,
  approved_by_eid, approved_date, rejection_reason,
  created_by_eid, created_date, created_at, updated_at, deleted_at
`;

async function hydrateSubReplacementRows(
  rows: SubReplacementRequest[],
): Promise<SubReplacementRequestRow[]> {
  if (rows.length === 0) return [];

  const facIds = Array.from(new Set(rows.map((r) => r.facility_id).filter(Boolean))) as string[];
  const accIds = Array.from(new Set(rows.map((r) => r.account_id).filter(Boolean))) as string[];
  const empIds = Array.from(new Set([
    ...rows.map((r) => r.requested_by_eid).filter(Boolean),
    ...rows.map((r) => r.approved_by_eid).filter((v): v is string => Boolean(v)),
  ])) as string[];
  const asnIds = Array.from(new Set(rows.map((r) => r.outgoing_assignment_id).filter((v): v is string => Boolean(v))));

  const [facRes, accRes, empRes, asnRes] = await Promise.all([
    facIds.length === 0
      ? Promise.resolve({ data: [] as unknown[], error: null })
      : supabase.from('facilities').select('facility_id, facility_name, market_region').in('facility_id', facIds),
    accIds.length === 0
      ? Promise.resolve({ data: [] as unknown[], error: null })
      : supabase.from('accounts').select('account_id, account_name').in('account_id', accIds),
    empIds.length === 0
      ? Promise.resolve({ data: [] as unknown[], error: null })
      : supabase.from('v_employees_am').select('employee_id, employee_name_first, employee_name_last').in('employee_id', empIds),
    asnIds.length === 0
      ? Promise.resolve({ data: [] as unknown[], error: null })
      // The assignment names its work once, from the one list. UI/types call
      // that display string role_title; we read the catalogue label for it.
      // Item 896: the line's pay basis says which unit the request's pay is in.
      : supabase.from('service_assignments').select('assignment_id, service_code, service_catalog(display_name), provider_id, sub_rate_type, sub_hourly_rate').in('assignment_id', asnIds),
  ]);

  const facMap: Record<string, { facility_name: string | null; market_region: string | null }> = {};
  for (const f of (facRes.data ?? []) as Array<{ facility_id: string; facility_name: string | null; market_region: string | null }>) {
    facMap[f.facility_id] = { facility_name: f.facility_name, market_region: f.market_region };
  }
  const accMap: Record<string, string | null> = {};
  for (const a of (accRes.data ?? []) as Array<{ account_id: string; account_name: string | null }>) {
    accMap[a.account_id] = a.account_name;
  }
  const empMap: Record<string, { employee_name_first: string | null; employee_name_last: string | null }> = {};
  for (const e of (empRes.data ?? []) as Array<{ employee_id: string; employee_name_first: string | null; employee_name_last: string | null }>) {
    empMap[e.employee_id] = { employee_name_first: e.employee_name_first, employee_name_last: e.employee_name_last };
  }
  const asnMap: Record<string, {
    assignment_id: string;
    role_title: string | null;
    provider_id: string | null;
    sub_rate_type: string | null;
    sub_hourly_rate: number | null;
  }> = {};
  for (const a of (asnRes.data ?? []) as unknown as Array<{
    assignment_id: string;
    service_catalog: { display_name: string | null }[] | { display_name: string | null } | null;
    provider_id: string | null;
    sub_rate_type: string | null;
    sub_hourly_rate: number | null;
  }>) {
    asnMap[a.assignment_id] = {
      assignment_id: a.assignment_id,
      role_title: catalogLabel(a.service_catalog),
      provider_id: a.provider_id,
      sub_rate_type: a.sub_rate_type ?? null,
      sub_hourly_rate: a.sub_hourly_rate ?? null,
    };
  }

  // AFB-0051 — resolve sub names via service_providers.partner_name (NOT
  // provider_name — that column does not exist). Two sources of provider ids:
  //   • incoming (new) sub  -> sub_replacement_requests.incoming_provider_id
  //   • previous (outgoing) -> outgoing_assignment_id -> service_assignments.provider_id
  const provIds = Array.from(new Set([
    ...rows.map((r) => r.incoming_provider_id).filter((v): v is string => Boolean(v)),
    ...Object.values(asnMap).map((a) => a.provider_id).filter((v): v is string => Boolean(v)),
  ]));
  const provRes = provIds.length === 0
    ? { data: [] as unknown[], error: null }
    : await supabase.from('service_providers').select('provider_id, partner_name').in('provider_id', provIds);
  const provMap: Record<string, string | null> = {};
  for (const p of (provRes.data ?? []) as Array<{ provider_id: string; partner_name: string | null }>) {
    provMap[p.provider_id] = p.partner_name;
  }

  return rows.map((r) => {
    const asn = r.outgoing_assignment_id ? asnMap[r.outgoing_assignment_id] ?? null : null;
    return {
      ...r,
      facilities: r.facility_id ? facMap[r.facility_id] ?? null : null,
      accounts: r.account_id ? { account_name: accMap[r.account_id] ?? null } : null,
      requester: r.requested_by_eid ? empMap[r.requested_by_eid] ?? null : null,
      approver: r.approved_by_eid ? empMap[r.approved_by_eid] ?? null : null,
      outgoing_assignment: asn
        ? { ...asn, provider_name: asn.provider_id ? provMap[asn.provider_id] ?? null : null }
        : null,
      incoming_provider: r.incoming_provider_id
        ? { partner_name: provMap[r.incoming_provider_id] ?? null }
        : null,
    };
  });
}

// ---------------------------------------------------------------------------
// Data result + read hook
// ---------------------------------------------------------------------------

export interface SubReplacementDataResult {
  data: SubReplacementRequestRow[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => void;
  updateStatus: (
    requestId: string,
    next: SubReplacementStatus,
    extra?: { rejection_reason?: string | null },
  ) => Promise<void>;
}

interface QueryFilter {
  /** Only rows requested by this EID. Used by the AM "my requests" view. */
  requestedBy?: string;
  /** Only rows in these statuses. Used by the approval queue. */
  statuses?: SubReplacementStatus[];
}

export function useSubReplacementRequests(
  filter: QueryFilter = {},
): SubReplacementDataResult {
  const [data, setData] = useState<SubReplacementRequestRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [tick, setTick] = useState(0);

  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  // Stable serialization of the filter so the effect doesn't re-run on
  // object identity changes.
  const filterKey = JSON.stringify({
    requestedBy: filter.requestedBy ?? null,
    statuses: filter.statuses ?? null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading((prev) => (lastUpdated === null ? true : prev));
      setError(null);
      try {
        let q = supabase
          .from('sub_replacement_requests')
          .select(REQUEST_FLAT_SELECT)
          .is('deleted_at', null);
        if (filter.requestedBy) q = q.eq('requested_by_eid', filter.requestedBy);
        if (filter.statuses && filter.statuses.length > 0) {
          q = q.in('status', filter.statuses);
        }
        const { data: rows, error: e } = await q.order('created_at', {
          ascending: false,
        });
        if (cancelled) return;
        if (e) throw e;
        const hydrated = await hydrateSubReplacementRows(
          (rows ?? []) as unknown as SubReplacementRequest[],
        );
        if (cancelled) return;
        setData(hydrated);
        setLastUpdated(new Date());
      } catch (err) {
        if (cancelled) return;
        console.error('[sub-replacement] load failed', err);
        setError(
          errorMessage(err, 'Failed to load Sub Replacement requests.'),
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, filterKey]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  const updateStatus = useCallback(
    async (
      requestId: string,
      next: SubReplacementStatus,
      extra: { rejection_reason?: string | null } = {},
    ) => {
      // Optimistic write — flip locally, then commit. Roll back on error.
      let prev: SubReplacementRequestRow | null = null;
      setData((rows) =>
        rows.map((r) => {
          if (r.request_id !== requestId) return r;
          prev = r;
          return {
            ...r,
            status: next,
            rejection_reason: extra.rejection_reason ?? r.rejection_reason,
          };
        }),
      );
      try {
        const payload: Record<string, unknown> = { status: next };
        if (extra.rejection_reason !== undefined) {
          payload.rejection_reason = extra.rejection_reason;
        }
        const { error: e } = await supabase
          .from('sub_replacement_requests')
          .update(payload)
          .eq('request_id', requestId);
        if (e) throw e;
      } catch (err) {
        console.error('[sub-replacement] status update failed', err);
        if (prev) {
          const restore = prev;
          setData((rows) =>
            rows.map((r) => (r.request_id === requestId ? restore : r)),
          );
        }
        throw err;
      }
    },
    [],
  );

  return useMemo(
    () => ({ data, isLoading, error, lastUpdated, refetch, updateStatus }),
    [data, isLoading, error, lastUpdated, refetch, updateStatus],
  );
}

// ---------------------------------------------------------------------------
// Detail fetch — single row, used by the request-detail screen.
// ---------------------------------------------------------------------------

export function useSubReplacementRequest(requestId: string | null) {
  const [data, setData] = useState<SubReplacementRequestRow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!requestId) {
      setData(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: row, error: e } = await supabase
          .from('sub_replacement_requests')
          .select(REQUEST_FLAT_SELECT)
          .eq('request_id', requestId)
          .maybeSingle();
        if (cancelled) return;
        if (e) throw e;
        if (!row) {
          setData(null);
        } else {
          const hydrated = await hydrateSubReplacementRows([row as unknown as SubReplacementRequest]);
          if (cancelled) return;
          setData(hydrated[0] ?? null);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[sub-replacement] detail load failed', err);
        setError(
          errorMessage(err, 'Failed to load the request.'),
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, tick]);

  return {
    data,
    isLoading,
    error,
    refetch: () => setTick((n) => n + 1),
  };
}

// ---------------------------------------------------------------------------
// Insert helper
// ---------------------------------------------------------------------------

export async function createSubReplacementRequest(
  input: NewSubReplacementRequestInput,
): Promise<{ request_id: string } | null> {
  // request_id is auto-assigned by BEFORE INSERT trigger
  // (trg_sub_replacement_requests_assign_id) as SRR-NNNN. Status defaults
  // to 'Pending' server-side.
  const payload = {
    facility_id: input.facility_id,
    account_id: input.account_id,
    outgoing_assignment_id: input.outgoing_assignment_id,
    incoming_provider_id: input.incoming_provider_id,
    requested_by_eid: input.requested_by_eid,
    created_by_eid: input.requested_by_eid,
    reason: input.reason.trim() || null,
    reason_code: input.reason_code,
    urgency: input.urgency,
    hold_pay: input.hold_pay,
    // AFB-0027: blank pay = NULL = "keep current rate". Never coerce to 0.
    incoming_pay_rate: input.incoming_pay_rate,
    nmi_enabled: input.nmi_enabled,
    nmi_amount: input.nmi_amount,
    compliment_enabled: input.compliment_enabled,
    compliment_amount: input.compliment_amount,
    planned_change_date: input.planned_change_date,
    change_date_type: input.change_date_type,
    notes: input.notes,
  };
  const { data, error } = await supabase
    .from('sub_replacement_requests')
    .insert(payload)
    .select('request_id')
    .single();
  if (error) throw error;
  return (data as { request_id: string } | null) ?? null;
}

// ---------------------------------------------------------------------------
// Form-options hooks — facility picker + outgoing-assignment picker.
// ---------------------------------------------------------------------------

export interface FacilityOption {
  facility_id: string;
  facility_name: string | null;
  account_id: string | null;
  market_region: string | null;
}

export interface AssignmentOption {
  assignment_id: string;
  facility_id: string;
  role_title: string | null;
  provider_id: string | null;
  provider_name: string | null;
  assignment_status: string | null;
  // AFB-0027 prefill context — current pay + incentive config on the
  // outgoing assignment, used to default the request form's new fields.
  sub_rate_monthly: number | null;
  // Item 896: on a line that pays its sub by the hour, the pay box asks per hour.
  sub_rate_type: string | null;
  sub_hourly_rate: number | null;
  no_misses_bonus_enabled: boolean | null;
  no_misses_bonus_amount: number | null;
  compliment_incentive_enabled: boolean | null;
}

export function useFacilityOptions(): {
  facilities: FacilityOption[];
  isLoading: boolean;
  error: string | null;
} {
  const [facilities, setFacilities] = useState<FacilityOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Use active_facilities view (excludes inactive facilities). RLS
        // narrows to is_my_facility for AMs automatically.
        const { data, error: e } = await supabase
          .from('active_facilities')
          .select('facility_id, facility_name, account_id, market_region')
          .order('facility_name');
        if (cancelled) return;
        if (e) throw e;
        setFacilities((data ?? []) as FacilityOption[]);
      } catch (err) {
        if (cancelled) return;
        console.error('[sub-replacement] facility options load failed', err);
        setError(
          errorMessage(err, 'Failed to load facilities.'),
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { facilities, isLoading, error };
}

export function useAssignmentOptions(facilityId: string | null): {
  assignments: AssignmentOption[];
  isLoading: boolean;
  error: string | null;
} {
  const [assignments, setAssignments] = useState<AssignmentOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!facilityId) {
      setAssignments([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Flat select + client-join on service_providers — no PostgREST embed.
        // The assignment names its work once, from the one list; role_title is
        // the display string the UI puts that label in.
        const { data: flatSa, error: e } = await supabase
          .from('service_assignments')
          .select(
            'assignment_id, facility_id, service_code, service_catalog(display_name), provider_id, assignment_status, sub_rate_monthly, sub_rate_type, sub_hourly_rate, no_misses_bonus_enabled, no_misses_bonus_amount, compliment_incentive_enabled',
          )
          .eq('facility_id', facilityId)
          .eq('assignment_status', 'Active')
          .order('service_code');
        if (cancelled) return;
        if (e) throw e;
        const flatList = (flatSa ?? []) as unknown as Array<{
          assignment_id: string;
          facility_id: string;
          service_code: string | null;
          service_catalog: { display_name: string | null }[] | { display_name: string | null } | null;
          provider_id: string | null;
          assignment_status: string | null;
          sub_rate_monthly: number | null;
          sub_rate_type: string | null;
          sub_hourly_rate: number | null;
          no_misses_bonus_enabled: boolean | null;
          no_misses_bonus_amount: number | null;
          compliment_incentive_enabled: boolean | null;
        }>;
        const providerIds = Array.from(new Set(flatList.map((r) => r.provider_id).filter((v): v is string => Boolean(v))));
        const provRes = providerIds.length === 0
          ? { data: [] as unknown[], error: null }
          : await supabase.from('service_providers').select('provider_id, partner_name').in('provider_id', providerIds);
        if (cancelled) return;
        const provMap: Record<string, string | null> = {};
        for (const p of (provRes.data ?? []) as Array<{ provider_id: string; partner_name: string | null }>) {
          provMap[p.provider_id] = p.partner_name;
        }
        const mapped = flatList.map((row) => ({
          assignment_id: row.assignment_id,
          facility_id: row.facility_id,
          role_title: catalogLabel(row.service_catalog),
          provider_id: row.provider_id,
          provider_name: row.provider_id ? provMap[row.provider_id] ?? null : null,
          assignment_status: row.assignment_status,
          sub_rate_monthly: row.sub_rate_monthly,
          sub_rate_type: row.sub_rate_type ?? null,
          sub_hourly_rate: row.sub_hourly_rate ?? null,
          no_misses_bonus_enabled: row.no_misses_bonus_enabled,
          no_misses_bonus_amount: row.no_misses_bonus_amount,
          compliment_incentive_enabled: row.compliment_incentive_enabled,
        }));
        setAssignments(mapped);
      } catch (err) {
        if (cancelled) return;
        console.error('[sub-replacement] assignment options load failed', err);
        setError(
          errorMessage(err, 'Failed to load assignments.'),
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  return { assignments, isLoading, error };
}
